import { createContext, useContext } from "react";
import { PageContext, type BaseChoice } from "./page";
import type { BaseMaxChars, BaseStyle, LeadKind, PreviewPiece } from "../types";
import { chosenBase } from "./ChristmasBase";
import { baseKindOf } from "./christmas";
import type { TogetherPiece } from "./OrderAll";
import { bill, fill, MAX_UNITS, type Bill } from "./multiBuy";
import { billIn, ownsQuantity } from "./Quantity";

/** Which pieces of a page of several they have chosen, at which size and how
 *  many of each, for the page to add up as they choose.
 *
 *  The pieces are independent: they can have one, some or all, so each card
 *  adds its piece to an order rather than opening a checkout of its own, and
 *  the bar at the bottom carries what the order comes to. The checkout is the
 *  same one "Order them together" always was (OrderAll.tsx), opened on what is
 *  chosen here; nothing about a price is worked out in a new way. The bill is
 *  bill() in multiBuy.ts, which the server works out again and refuses a
 *  payment that is not the one shown.
 *
 *  Kept in the page (PageInfo.picks, PageInfo.qtys) rather than in the cards,
 *  so the cards, the rail, the summary, the bar and the sheet all read the
 *  one answer. Not on the device: a later visit starts from nothing chosen,
 *  as the quantities always did. */

/** A piece's key in a selection: ids are a lead's own, and every preview of
 *  one piece calls its piece "", so another preview's could be this one's.
 *  A token of "" is this page's own. */
export function pieceKey(token: string, id: string): string {
  return `${token}:${id}`;
}

/** What a selected piece's "how many" is kept under in PageInfo.qtys: apart
 *  from the keys the per-piece sheets use (qtyKey in Quantity.tsx). */
export function qtySel(key: string): string {
  return `sel:${key}`;
}

type Offer = NonNullable<PreviewPiece["price"]>;
type Chip = NonNullable<Offer["sizes"]>[number];

/** A piece in the order, at the size and the price its chip showed. */
export interface BasketLine {
  piece: TogetherPiece;
  key: string;
  kind: LeadKind;
  size: string;
  chip: Chip;
  offer: Offer;
  qty: number;
  /** The Christmas base it is to stand on, with its message cleaned as it
   *  will be sent, where they switched one on for it on its card and the page
   *  offers it at this size (ChristmasBase.tsx); null for its plain base. It
   *  costs nothing, so no sum here moves for it. */
  base: { base: BaseStyle; message: string } | null;
}

export interface Basket {
  /** In the order the cards show them. */
  lines: BasketLine[];
  /** Whether the page says how many and works the bill out (the multi-buy);
   *  otherwise Stripe's form says how many, and `qty` is always one. */
  owns: boolean;
  billed: Bill | null;
  /** What the order comes to, written as the server writes a price; null when
   *  the chips do not carry what a sum is made of. */
  total: string | null;
  /** What the multi-buy took off, written the same way; null for nothing. */
  saved: string | null;
  /** The pieces in all, counting two of one as two. */
  units: number;
}

/** The pieces of a page that can be chosen in it: priced, with their sizes,
 *  on a page that takes the payment itself, and two or more of them. A page
 *  without that has each piece order on its own, as it always did. */
export function selectable(pieces: TogetherPiece[], checkout: boolean): boolean {
  return checkout && pieces.filter((p) => !!p.piece.price?.sizes?.length).length >= 2;
}

/** What is chosen, as the order sheet would list it. `picks` is a size per
 *  piece key, or null for one left out; a size the piece no longer has is
 *  left out too, rather than charged. */
export function basketOf(
  pieces: TogetherPiece[],
  picks: Record<string, string | null>,
  qtys: Record<string, number>,
  fallbackKind: LeadKind,
  checkout: boolean,
  bases: Record<string, BaseChoice> = {},
  baseLimits: BaseMaxChars | null = null
): Basket {
  const chosen = pieces.flatMap((piece) => {
    const key = pieceKey(piece.token, piece.piece.id);
    const size = picks[key];
    const offer = piece.piece.price;
    const chip = size ? offer?.sizes?.find((s) => s.size === size) : undefined;
    if (!size || !offer || !chip) return [];
    return [{ piece, key, kind: piece.piece.kind ?? fallbackKind, size, chip, offer }];
  });
  const format = chosen[0]?.offer.format ?? "";
  const owns = checkout && chosen.length > 0 && chosen.every((c) => ownsQuantity(c.offer) && c.offer.format === format);
  // As the sheet counts: one at the least, and no more in all than a payment
  // takes. A piece that would be the one past the cap is left out rather than
  // forced in at one, which would put the order over what checkout accepts.
  let room = MAX_UNITS;
  const lines: BasketLine[] = chosen.flatMap((c) => {
    if (room < 1) return [];
    const asked = owns ? Math.min(Math.max(1, Math.floor(qtys[qtySel(c.key)] ?? 1) || 1), MAX_UNITS) : 1;
    const qty = Math.min(asked, room);
    room -= qty;
    // Its base, under the same key as its "how many" (PageInfo.bases).
    return [{ ...c, qty, base: chosenBase(bases[qtySel(c.key)], baseKindOf(c.kind, c.offer), c.size, baseLimits) }];
  });
  const billed = owns ? bill(lines.map((l) => billIn(l.chip, l.qty))) : null;
  let total: string | null = null;
  if (billed) total = fill(format, billed.total);
  else if (lines.length && format && lines.every((l) => typeof l.chip.cents === "number" && l.offer.format === format)) {
    total = fill(format, lines.reduce((sum, l) => sum + (l.chip.cents ?? 0), 0));
  }
  return {
    lines,
    owns,
    billed,
    total,
    saved: billed && billed.off > 0 ? fill(format, billed.off) : null,
    units: lines.reduce((sum, l) => sum + l.qty, 0),
  };
}

/** What the cards, the rail, the summary and the bar read: the order so far,
 *  and whether this page lets them choose at all. */
export interface BasketInfo {
  on: boolean;
  basket: Basket;
  /** Open the sheet of "Order them together" on what is chosen. */
  open: () => void;
}

export const BasketContext = createContext<BasketInfo | null>(null);

/** Whether the order summary (Basket.tsx) is showing when it would arrive
 *  and that nothing is owed on delivery: on a page that lets them choose,
 *  not yet paid for, with something in the order. The price and details
 *  cards further down said the same lines again, so they leave them to it
 *  while it does, and say them themselves when it does not (nothing chosen
 *  yet), so the dates are never missing from the page. */
export function useBasketSaysDelivery(): boolean {
  const page = useContext(PageContext);
  const info = useContext(BasketContext);
  return !!info?.on && !(page?.ordered && !page.admin) && info.basket.lines.length > 0;
}
